import { formatMoney } from "helpers/money";
import type { BillingItemData } from "services/billingItems/types";
import { BillingItem } from "services/checkoutBasket/checkoutBasketTypes";
import { VirtualOfficeBillingPeriod } from "services/companyProducts/companyProductsTypes";

export class BillingItemsStore {
  getData(billingItem: BillingItem, param?: unknown): BillingItemData {
    switch (billingItem) {
      case BillingItem.VirtualOffice: {
        return this._getVirtualOfficeData(param as VirtualOfficeBillingPeriod);
      }
      case BillingItem.Taxes:
        return {
          title: "+Taxes",
          description: "Take the stress out of tax admin by letting ANNA + Taxes do the work for you",
          featureDescription: "Tax admin made easy",
          price: "Free for 4 months",
          remark: `after that ${formatMoney(29)} + VAT/month`,
          actualPriceInCent: 0,
          vatPriceInCent: 0,
        };
      case BillingItem.VatRegistration:
        return {
          title: "VAT registration with HMRC",
          description:
            "We'll take care of the paperwork and submit the application to HMRC on your behalf. It's an on-demand service",
          featureDescription: "On-demand VAT registration for your business",
          price: formatMoney(39.9),
          vat: formatMoney(7.98),
          actualPriceInCent: 3990,
          vatPriceInCent: 798,
        };
      case BillingItem.ConfirmationStatement:
        return {
          title: "Confirmation statement filing",
          description:
            "ANNA can help you to file your company’s confirmation statement (annual return) " +
            "in time for the deadline for you",
          featureDescription: "ANNA will help to prepare annual filing for your business",
          price: formatMoney(79.0),
          vat: formatMoney(15.8),
          actualPriceInCent: 7990,
          vatPriceInCent: 1580,
        };
      case BillingItem.DirectorManagement:
        return {
          title: "Officer management service",
          description: "Officer appointments and changes on demand,  all with full legal compliance",
          price: formatMoney(39.9),
          vat: formatMoney(7.98),
          actualPriceInCent: 3990,
          vatPriceInCent: 798,
        };
      case BillingItem.CancellationCover:
        return {
          title: "Company cancellation cover",
          description:
            "Have you changed mind and decided you no longer need your company? " +
            "We’ll take care of the paperwork and close the company for you",
          featureDescription: "We'll handle the paperwork and close your company for you",
          price: formatMoney(49.9),
          vat: formatMoney(9.98),
          actualPriceInCent: 4990,
          vatPriceInCent: 998,
        };
      case BillingItem.PayeRegistration:
        return {
          title: "PAYE Employer registration",
          description: "We'll handle the PAYE registration on your behalf. It's an on-demand service",
          featureDescription: "On-demand PAYE employer registration for your business",
          price: formatMoney(39.9),
          vat: formatMoney(7.98),
          actualPriceInCent: 3990,
          vatPriceInCent: 798,
        };
      case BillingItem.SharesManagement:
        return {
          title: "Share management",
          description:
            "Issue and transfer of shares, documentation management and record updates to stay compliant with Companies House",
          featureDescription: "Issue and transfer of shares  and record updates",
          price: formatMoney(99.0),
          vat: formatMoney(19.8),
          actualPriceInCent: 9900,
          vatPriceInCent: 1980,
        };
      case BillingItem.FormationExpress:
        return {
          title: "Same-day registration",
          description: "Same day company registration",
          price: formatMoney(199.0),
          vat: formatMoney(39.8),
          actualPriceInCent: 19900,
          vatPriceInCent: 3980,
        };
    }
  }

  private _getVirtualOfficeData(
    billingPeriod: VirtualOfficeBillingPeriod = VirtualOfficeBillingPeriod.Monthly,
  ): BillingItemData {
    const prices = {
      [VirtualOfficeBillingPeriod.Monthly]: {
        price: formatMoney(19.9, { monthly: true }),
        vat: formatMoney(3.98),
      },
      [VirtualOfficeBillingPeriod.Yearly]: {
        price: formatMoney(15, { monthly: true }),
        vat: formatMoney(3),
      },
    };
    const price = prices[billingPeriod].price;
    const vat = prices[billingPeriod].vat;
    const priceSuffix = ", billed " + (billingPeriod === VirtualOfficeBillingPeriod.Monthly ? "monthly" : "annually");

    return {
      title: "Virtual Office",
      description: "Keep your personal address private with same day scanning and mail forwarding",
      price,
      priceText: price + priceSuffix,
      vat,
      priceTotal: billingPeriod === VirtualOfficeBillingPeriod.Yearly ? formatMoney(180) + "/year" : undefined,
      vatTotal: billingPeriod === VirtualOfficeBillingPeriod.Yearly ? formatMoney(36) : undefined,
      actualPriceInCent: billingPeriod === VirtualOfficeBillingPeriod.Yearly ? 18000 : 1990,
      vatPriceInCent: billingPeriod === VirtualOfficeBillingPeriod.Yearly ? 3600 : 398,
    };
  }
}
